'use client';

/**
 * Why a workflow is the way it is.
 *
 * A workflow template records WHAT the steps are. Nothing recorded why: the
 * meeting where the intake route was agreed, the decision to send navigators
 * rather than the front desk, the payer rule the whole design has to satisfy,
 * the document it was all based on. That content exists — in email, in call
 * recordings, in somebody's notes — and none of it was in the product.
 *
 * FOUR KINDS, AND WHY THEY ARE NOT ONE
 *
 *   meeting      what was discussed, and with whom
 *   decision     what was settled, and on what date
 *   requirement  a constraint the design has to satisfy — a payer rule, a
 *                funder's reporting demand. NOT a decision: nobody chose it,
 *                and nobody can revisit it. Collapsing the two would invite
 *                somebody to "revisit" a contractual obligation.
 *   reference    documentation this design rests on
 *
 * SUPERSEDING IS THE FEATURE
 *
 * Key decisions are only useful if a reversed decision is visibly reversed.
 * Without it two contradictory decisions sit side by side, both looking
 * current, and whoever reads them — a person or the assistant — picks one. A
 * superseded decision stays readable and stays struck through, because the fact
 * that it was reversed is itself part of the reasoning.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  CalendarDays,
  ExternalLink,
  Gavel,
  Loader2,
  Lock,
  Plus,
  Trash2,
  Users,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { RichTextEditor, renderMarkdownToHtml } from '@/components/ui/rich-text-editor';
import { NoteAttachments } from './NoteAttachments';
import {
  workflowsAPI,
  WorkflowNote,
  WorkflowNoteAttachment,
  WorkflowNoteInput,
  WorkflowNoteType,
  WorkflowStep,
} from '@/services/api/workflows';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface WorkflowNotesPanelProps {
  templateId: number;
  /** Steps, so a note can be pinned to one. Optional: the panel is useful on a
   *  template whose steps have not been written yet, which is exactly when the
   *  meeting notes and decisions are being made. */
  steps?: WorkflowStep[];
  /** Opened from a step rather than from the toolbar: the list is filtered to
   *  that step and a new note is pinned to it without anybody having to pick it
   *  out of a dropdown. Arriving from a box and then having to say which box
   *  you came from is the kind of re-asking that stops people writing notes. */
  focusStepId?: number | null;
  onClearFocus?: () => void;
}

const TYPE_META: Record<
  WorkflowNoteType,
  { icon: React.ReactNode; label: string; help: string }
> = {
  meeting: {
    icon: <Users className="h-4 w-4" />,
    label: 'Meeting',
    help: 'What was discussed, and with whom.',
  },
  decision: {
    icon: <Gavel className="h-4 w-4" />,
    label: 'Decision',
    help: 'What was settled, and on what date. Can be revisited later.',
  },
  requirement: {
    icon: <Lock className="h-4 w-4" />,
    label: 'Requirement',
    help: 'A constraint this design has to satisfy — a payer rule, a funder demand. Not something anyone here chose.',
  },
  reference: {
    icon: <BookOpen className="h-4 w-4" />,
    label: 'Reference',
    help: 'Documentation this design rests on.',
  },
};

const EMPTY: WorkflowNoteInput = { note_type: 'meeting', body: '', title: '', attachments: [] };

export const WorkflowNotesPanel: React.FC<WorkflowNotesPanelProps> = ({
  templateId,
  steps = [],
  focusStepId = null,
  onClearFocus,
}) => {
  const { t } = useAppTranslation();
  const [notes, setNotes] = useState<WorkflowNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<WorkflowNoteInput>(EMPTY);
  const [filter, setFilter] = useState<WorkflowNoteType | 'all'>('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setNotes(await workflowsAPI.listWorkflowNotes(templateId));
      setError(null);
    } catch {
      setError(t('workflows.notes.loadFailed', 'The notes could not be loaded. Try again.'));
    } finally {
      setLoading(false);
    }
  }, [templateId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Everything the panel is currently about: the whole workflow, or one step
   *  when opened from a box. The chips count from this, so their numbers never
   *  contradict the list underneath them. */
  const inScope = useMemo(
    () => (focusStepId ? notes.filter((n) => n.workflow_step_id === focusStepId) : notes),
    [notes, focusStepId]
  );

  const visible = useMemo(
    () => (filter === 'all' ? inScope : inScope.filter((n) => n.note_type === filter)),
    [inScope, filter]
  );

  const focusedStep = useMemo(
    () => steps.find((s) => s.id === focusStepId) ?? null,
    [steps, focusStepId]
  );

  /** Arriving from a step opens the form already pinned to it. */
  useEffect(() => {
    if (!focusStepId) return;
    setDraft((d) => ({ ...d, workflow_step_id: focusStepId }));
    setShowForm(true);
  }, [focusStepId]);

  /** Decisions that still stand, for the supersedes picker. Offering a
   *  superseded decision would build a chain that reads as two reversals. */
  const supersedable = useMemo(
    () => notes.filter((n) => n.note_type === 'decision' && !n.is_superseded),
    [notes]
  );

  const handleSave = async () => {
    if (!draft.body.trim()) return;
    setSaving(true);
    try {
      await workflowsAPI.createWorkflowNote(templateId, {
        ...draft,
        body: draft.body.trim(),
        title: draft.title?.trim() || null,
      });
      setDraft(EMPTY);
      setShowForm(false);
      await load();
      setError(null);
    } catch {
      setError(t('workflows.notes.saveFailed', 'The note could not be saved. Nothing was recorded.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (note: WorkflowNote) => {
    const confirmed = window.confirm(
      t('workflows.notes.deleteConfirm', 'Remove this note?\n\nIt stays in the record for anything that refers to it.')
    );
    if (!confirmed) return;
    try {
      await workflowsAPI.deleteWorkflowNote(templateId, note.id);
      await load();
    } catch {
      setError(t('workflows.notes.deleteFailed', 'The note could not be removed.'));
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {t('workflows.notes.heading', 'Background and decisions')}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
            {t(
              'workflows.notes.intro',
              'The meetings, decisions and constraints behind this workflow. Whoever edits it in a year needs these more than they need the step list.'
            )}
          </p>
        </div>
        <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setShowForm(true)}>
          {t('workflows.notes.add', 'Add note')}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {focusedStep && (
        /* A filtered list has to say so. Without this the panel looks like the
           workflow has one note, and somebody goes and writes a second copy of
           the one they could not find. */
        <div className="flex items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm dark:border-blue-900 dark:bg-blue-950">
          <span className="min-w-0 text-blue-900 dark:text-blue-200">
            Showing background for step {focusedStep.step_order} ·{' '}
            <strong className="font-medium">{focusedStep.step_name}</strong>
          </span>
          <button
            type="button"
            onClick={onClearFocus}
            className="shrink-0 text-xs font-medium text-blue-700 hover:underline dark:text-blue-300"
          >
            Show the whole workflow
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {(['all', 'meeting', 'decision', 'requirement', 'reference'] as const).map((key) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              filter === key
                ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300'
            }`}
          >
            {key === 'all' ? t('workflows.notes.all', 'All') : TYPE_META[key].label}
            {key !== 'all'
              ? ` ${inScope.filter((n) => n.note_type === key).length}`
              : ` ${inScope.length}`}
          </button>
        ))}
      </div>

      {showForm && (
        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('workflows.notes.newNote', 'New note')}
            </span>
            <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-700">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                {t('workflows.notes.type', 'Kind')}
              </label>
              <select
                value={draft.note_type}
                onChange={(e) => setDraft({ ...draft, note_type: e.target.value as WorkflowNoteType })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              >
                {(Object.keys(TYPE_META) as WorkflowNoteType[]).map((k) => (
                  <option key={k} value={k}>
                    {TYPE_META[k].label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{TYPE_META[draft.note_type].help}</p>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                {t('workflows.notes.title', 'Title')}
              </label>
              <input
                type="text"
                value={draft.title ?? ''}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              />
            </div>

            {draft.note_type === 'meeting' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                  {t('workflows.notes.meetingDate', 'Meeting date')}
                </label>
                <input
                  type="date"
                  value={draft.meeting_date?.slice(0, 10) ?? ''}
                  onChange={(e) => setDraft({ ...draft, meeting_date: e.target.value || null })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                />
              </div>
            )}

            {draft.note_type === 'decision' && (
              <>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                    {t('workflows.notes.decidedOn', 'Decided on')}
                  </label>
                  <input
                    type="date"
                    value={draft.decided_on ?? ''}
                    onChange={(e) => setDraft({ ...draft, decided_on: e.target.value || null })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                  />
                  {/* Separate from "created", because a decision is usually
                      written up days after it was taken and the date it was
                      TAKEN is what orders it against the others. */}
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('workflows.notes.decidedOnHelp', 'When it was agreed, not when you are writing it up.')}
                  </p>
                </div>

                {supersedable.length > 0 && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                      {t('workflows.notes.supersedes', 'Replaces an earlier decision')}
                    </label>
                    <select
                      value={draft.supersedes_note_id ?? ''}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          supersedes_note_id: e.target.value ? Number(e.target.value) : null,
                        })
                      }
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                    >
                      <option value="">{t('workflows.notes.supersedesNone', 'Nothing — this is new')}</option>
                      {supersedable.map((n) => (
                        <option key={n.id} value={n.id}>
                          {n.title || n.body.slice(0, 60)}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {t(
                        'workflows.notes.supersedesHelp',
                        'The earlier decision stays readable and is marked as replaced, so nobody acts on it by mistake.'
                      )}
                    </p>
                  </div>
                )}
              </>
            )}

            {steps.length > 0 && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                  {t('workflows.notes.aboutStep', 'About one step')}
                </label>
                <select
                  value={draft.workflow_step_id ?? ''}
                  onChange={(e) =>
                    setDraft({ ...draft, workflow_step_id: e.target.value ? Number(e.target.value) : null })
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                >
                  <option value="">{t('workflows.notes.wholeWorkflow', 'The whole workflow')}</option>
                  {steps.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.step_order}. {s.step_name}
                    </option>
                  ))}
                </select>
              </div>
            )}


            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                {t('workflows.notes.body', 'Note')}
                <span className="text-red-500"> *</span>
              </label>
              {/* Long-form and formatted. A decision worth recording is a
                  paragraph with a list in it, and a five-row plain textarea
                  made people write one line and keep the rest in their head. */}
              <RichTextEditor
                value={draft.body}
                onChange={(body) => setDraft({ ...draft, body })}
                placeholder="What was decided, and why. **Bold**, lists and links all work."
                minHeight="180px"
              />
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              {t('workflows.notes.attachments', 'What it refers to')}
            </label>
            <NoteAttachments
              value={draft.attachments ?? []}
              onChange={(attachments) => setDraft({ ...draft, attachments })}
            />
          </div>

          <div className="mt-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowForm(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="primary"
              /* A note with no body reads as documented when it is not, which is
                 worse than an obvious gap — so it cannot be saved empty. */
              disabled={saving || !draft.body.trim()}
              icon={saving ? <Loader2 className="h-4 w-4 animate-spin" /> : undefined}
              onClick={() => void handleSave()}
            >
              {t('workflows.notes.save', 'Save note')}
            </Button>
          </div>
        </div>
      )}

      {visible.length === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          {t(
            'workflows.notes.empty',
            'Nothing recorded yet. The first thing worth writing down is usually the decision somebody will question in six months.'
          )}
        </p>
      ) : (
        <div className="space-y-3">
          {visible.map((note) => (
            <div
              key={note.id}
              className={`rounded-lg border p-4 ${
                note.is_superseded
                  ? 'border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/40'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" size="sm">
                      <span className="mr-1">{TYPE_META[note.note_type].icon}</span>
                      {TYPE_META[note.note_type].label}
                    </Badge>
                    {note.is_superseded && (
                      /* Struck through AND labelled. A reversed decision that
                         looks current is the one failure this panel must not
                         have. */
                      <Badge variant="secondary" size="sm">
                        {t('workflows.notes.superseded', 'Replaced by a later decision')}
                      </Badge>
                    )}
                    {note.step_name && (
                      <Badge variant="outline" size="sm">
                        {note.step_name}
                      </Badge>
                    )}
                  </div>

                  {note.title && (
                    <p
                      className={`mt-2 font-medium text-gray-900 dark:text-gray-100 ${
                        note.is_superseded ? 'line-through decoration-gray-400' : ''
                      }`}
                    >
                      {note.title}
                    </p>
                  )}

                  {/* Rendered through the SAME converter the editor previews
                      with, so a saved note looks like it did while it was
                      being written. It escapes markup before converting, which
                      is what makes this safe. */}
                  <div
                    className="mt-1 text-sm text-gray-700 dark:text-gray-300"
                    dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(note.body) }}
                  />

                  {note.attachments?.length > 0 && (
                    <div className="mt-2">
                      <NoteAttachments value={note.attachments} onChange={() => {}} editable={false} />
                    </div>
                  )}

                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                    {note.decided_on && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="h-3 w-3" />
                        {t('workflows.notes.decidedOnLabel', 'Decided {{date}}', {
                          date: new Date(note.decided_on).toLocaleDateString(),
                        })}
                      </span>
                    )}
                    {note.meeting_date && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="h-3 w-3" />
                        {new Date(note.meeting_date).toLocaleDateString()}
                      </span>
                    )}
                    {note.created_by_name && <span>{note.created_by_name}</span>}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => void handleDelete(note)}
                  title={t('workflows.notes.delete', 'Remove')}
                  className="shrink-0 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-800"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkflowNotesPanel;
